import { ensureRouteQueryData } from "@/lib/route-loader";
import {
  useAdminOrganizationGetCurrentAndActiveSemester,
  useAdminOrganizationGetOrganization,
} from "@/orval/admin-organization";
import { getAdminOrganizationGetOrganizationQueryOptions } from "@/orval/admin-organization";
import { createFileRoute, Link, Outlet } from "@tanstack/react-router";
import {
  HomeIcon,
  BookOpenIcon,
  FileTextIcon,
  EyeIcon,
  User2Icon,
  GraduationCapIcon,
  BuildingIcon,
  ShieldIcon,
  UsersIcon,
  UserCheckIcon,
  DollarSignIcon,
  ClipboardListIcon,
  CalendarDaysIcon,
  CalendarIcon,
  CreditCard,
  ChevronRight,
  CircleCheckBigIcon,
  CircleDollarSignIcon,
  TriangleAlertIcon,
  UserCogIcon,
  SettingsIcon,
  UserIcon,
  InfoIcon,
  MailPlusIcon,
} from "lucide-react";
import { SidebarOrganizationHeader } from "./-components/sidebar-organization-header";
import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
  SidebarSeparator,
  SidebarProvider,
} from "@/components/ui/sidebar";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { useAuthGetImpersonationStatus } from "@/orval/auth";
import { parseOrganizationIdParams } from "@/lib/route-params";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import type { LucideIcon } from "lucide-react";
import { SidebarViewSwitcher } from "./-components/sidebar-footer-actions";
import { SiteBuildMetadata } from "@/components/site-build-metadata";
import { ProductEmailInvitation } from "@/components/product-email-invitation";
import { useSidebar } from "@/hooks/use-sidebar";
import type { CurrentAndActiveSemesterDTO } from "@/orval/model";

type SidebarChildItem = {
  to: string;
  label: string;
  icon: LucideIcon;
  search?: { semesterId?: number };
};

type SidebarNavItem = {
  label: string;
  icon: LucideIcon;
  to?: string;
  children?: SidebarChildItem[];
};

const getAdminNavigationItems = (currentAndActiveSemester?: CurrentAndActiveSemesterDTO): SidebarNavItem[] => {
  const nextSemesterId =
    currentAndActiveSemester?.nextSemesterId ?? currentAndActiveSemester?.activeSemesterId ?? undefined;
  const activeSemesterId =
    currentAndActiveSemester?.activeSemesterId ?? currentAndActiveSemester?.nextSemesterId ?? undefined;

  return [
    {
      label: "Applications",
      icon: FileTextIcon,
      to: "/organizations/$organizationId/admin/applications",
    },
    {
      label: "Academics",
      icon: EyeIcon,
      children: [
        {
          to: "/organizations/$organizationId/admin/semesters",
          icon: CalendarIcon,
          label: "Semesters",
        },
        {
          to: "/organizations/$organizationId/admin/enrollment-days",
          icon: ClipboardListIcon,
          label: "Enrollment Days",
          search: { semesterId: nextSemesterId },
        },
        {
          to: "/organizations/$organizationId/admin/classes",
          icon: BookOpenIcon,
          label: "Classes",
        },
        {
          to: "/organizations/$organizationId/admin/schedule",
          icon: CalendarDaysIcon,
          label: "Schedule",
          search: { semesterId: activeSemesterId },
        },
      ],
    },
    {
      label: "People",
      icon: UserIcon,
      children: [
        {
          to: "/organizations/$organizationId/admin/all-members",
          icon: User2Icon,
          label: "All Members",
        },
        {
          to: "/organizations/$organizationId/admin/invitations",
          icon: MailPlusIcon,
          label: "Invitations",
        },
        {
          to: "/organizations/$organizationId/admin/teachers",
          icon: GraduationCapIcon,
          label: "Teachers",
        },
        {
          to: "/organizations/$organizationId/admin/admins",
          icon: ShieldIcon,
          label: "Admins",
        },
        {
          to: "/organizations/$organizationId/admin/families",
          icon: HomeIcon,
          label: "Families",
        },
        {
          to: "/organizations/$organizationId/admin/parents",
          icon: UserCheckIcon,
          label: "Parents",
        },
        {
          to: "/organizations/$organizationId/admin/students",
          icon: UsersIcon,
          label: "Students",
        },
      ],
    },
    {
      label: "Financials",
      icon: DollarSignIcon,
      children: [
        {
          to: "/organizations/$organizationId/admin/family-balances",
          icon: CircleCheckBigIcon,
          label: "Family Balances",
          search: { semesterId: nextSemesterId },
        },
        {
          to: "/organizations/$organizationId/admin/teacher-payments",
          icon: CircleDollarSignIcon,
          label: "Teacher Payments",
          search: { semesterId: nextSemesterId },
        },
        {
          to: "/organizations/$organizationId/admin/fee-collections",
          icon: TriangleAlertIcon,
          label: "Fees Collected",
          search: { semesterId: nextSemesterId },
        },
        {
          to: "/organizations/$organizationId/admin/payments",
          icon: CreditCard,
          label: "Payments",
          search: { semesterId: nextSemesterId },
        },
      ],
    },
    {
      label: "Forms",
      icon: ClipboardListIcon,
      to: "/organizations/$organizationId/admin/forms",
    },
    {
      label: "Settings",
      icon: BuildingIcon,
      children: [
        {
          to: "/organizations/$organizationId/admin/settings",
          icon: SettingsIcon,
          label: "Organization",
        },
        {
          to: "/organizations/$organizationId/admin/roles",
          icon: UserCogIcon,
          label: "Roles",
        },
      ],
    },
  ];
};

const OrganizationAdminPage = () => {
  const { organizationId } = Route.useParams();
  const { data: organization, isLoading } = useAdminOrganizationGetOrganization(organizationId);
  const { data: currentAndActiveSemester } = useAdminOrganizationGetCurrentAndActiveSemester(organizationId);

  if (!organization && !isLoading) {
    throw new Error("Organization not found");
  }

  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const { data: impersonationStatus } = useAuthGetImpersonationStatus();

  return (
    <SidebarProvider open={isSidebarOpen} onOpenChange={setIsSidebarOpen}>
      <div className="flex min-w-0 flex-1 flex-col min-h-full w-full">
        <div className="flex min-w-0 flex-row flex-1">
          <OrganizationAdminSidebar
            organizationId={organizationId}
            organizationName={organization?.name ?? undefined}
            logoUrl={organization?.logoImageUrl}
            isImpersonating={!!impersonationStatus?.isImpersonating}
            currentAndActiveSemester={currentAndActiveSemester}
          />

          <div className="flex min-w-0 flex-col flex-1">
            <ProductEmailInvitation />
            <Outlet />
          </div>
        </div>
      </div>
    </SidebarProvider>
  );
};

type OrganizationAdminSidebarProps = {
  organizationId: number;
  organizationName?: string;
  logoUrl?: string | null;
  isImpersonating: boolean;
  currentAndActiveSemester?: CurrentAndActiveSemesterDTO;
};

const OrganizationAdminSidebar = ({
  organizationId,
  organizationName,
  logoUrl,
  isImpersonating,
  currentAndActiveSemester,
}: OrganizationAdminSidebarProps) => {
  const { isMobile, open, setOpenMobile } = useSidebar();
  const adminNavigationItems = getAdminNavigationItems(currentAndActiveSemester);
  const isCollapsedDesktop = !open && !isMobile;
  const handleNavigationLinkClick = () => {
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  return (
    <Sidebar
      className={cn(
        isImpersonating ? "md:top-28 md:pb-28" : "md:top-16 md:pb-16",
        `w-64 border-r-muted border-r flex-none bg-background z-10`,
      )}
      collapsible="icon"
    >
      <SidebarHeader className="bg-background group-data-[collapsible=icon]:p-1">
        <SidebarOrganizationHeader
          logoUrl={logoUrl}
          organizationName={organizationName}
          organizationId={organizationId}
        />
      </SidebarHeader>

      <SidebarContent className="bg-background">
        <SidebarGroup className="pt-0">
          <SidebarGroupContent>
            <SidebarMenu>
              {adminNavigationItems.map((item) => {
                const hasChildren = !!item.children?.length;

                if (!hasChildren && item.to) {
                  return (
                    <SidebarMenuItem key={item.to}>
                      <SidebarMenuButton asChild tooltip={item.label}>
                        <Link
                          to={item.to}
                          activeProps={{
                            className: "bg-secondary text-secondary-foreground/100",
                          }}
                          from={Route.fullPath}
                          onClick={handleNavigationLinkClick}
                        >
                          <item.icon />
                          <span>{item.label}</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                }

                if (!item.children) {
                  return null;
                }

                const defaultOpen = true;

                if (isCollapsedDesktop) {
                  return (
                    <SidebarMenuItem key={item.label}>
                      <Popover>
                        <PopoverTrigger asChild>
                          <SidebarMenuButton tooltip={item.label}>
                            <item.icon />
                            <span>{item.label}</span>
                          </SidebarMenuButton>
                        </PopoverTrigger>
                        <PopoverContent side="right" align="start" className="w-56 p-2">
                          <p className="px-2 pb-2 text-xs font-medium text-muted-foreground">{item.label}</p>
                          <div className="flex flex-col gap-1">
                            {item.children.map((child) => (
                              <Link
                                key={child.to}
                                to={child.to}
                                from={Route.fullPath}
                                search={child.search}
                                preload="intent"
                                className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-foreground/80 hover:bg-accent hover:text-accent-foreground"
                                activeProps={{
                                  className: "bg-secondary text-secondary-foreground/100",
                                }}
                              >
                                <child.icon className="size-4" />
                                <span>{child.label}</span>
                              </Link>
                            ))}
                          </div>
                        </PopoverContent>
                      </Popover>
                    </SidebarMenuItem>
                  );
                }

                return (
                  <Collapsible key={item.label} defaultOpen={defaultOpen} className="group/collapsible">
                    <SidebarMenuItem>
                      <CollapsibleTrigger asChild>
                        <SidebarMenuButton tooltip={item.label}>
                          <item.icon />
                          <span>{item.label}</span>
                          <ChevronRight className="ml-auto transition-transform group-data-[state=open]/collapsible:rotate-90" />
                        </SidebarMenuButton>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <SidebarMenuSub>
                          {item.children.map((child) => (
                            <SidebarMenuSubItem key={child.to}>
                              <SidebarMenuSubButton asChild>
                                <Link
                                  to={child.to}
                                  from={Route.fullPath}
                                  search={child.search}
                                  preload="intent"
                                  activeProps={{
                                    className: "bg-secondary text-secondary-foreground/100",
                                  }}
                                  onClick={handleNavigationLinkClick}
                                >
                                  <child.icon />
                                  <span>{child.label}</span>
                                </Link>
                              </SidebarMenuSubButton>
                            </SidebarMenuSubItem>
                          ))}
                        </SidebarMenuSub>
                      </CollapsibleContent>
                    </SidebarMenuItem>
                  </Collapsible>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="bg-background group-data-[collapsible=icon]:items-center group-data-[collapsible=icon]:px-1">
        <SidebarSeparator className="mx-0 group-data-[collapsible=icon]:hidden" />
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarViewSwitcher
              label="Switch view"
              currentView="Admin"
              organizationId={organizationId}
              from={Route.fullPath}
            />
          </SidebarMenuItem>
        </SidebarMenu>
        <SidebarMenu className="hidden group-data-[collapsible=icon]:flex">
          <SidebarMenuItem>
            <Popover>
              <PopoverTrigger asChild>
                <SidebarMenuButton tooltip="Build metadata" className="justify-center">
                  <InfoIcon className="size-4" />
                  <span className="sr-only">Build metadata</span>
                </SidebarMenuButton>
              </PopoverTrigger>
              <PopoverContent side="right" align="center" className="w-64">
                <SiteBuildMetadata compact className="text-muted-foreground" />
              </PopoverContent>
            </Popover>
          </SidebarMenuItem>
        </SidebarMenu>
        <div className="px-2 pb-2 group-data-[collapsible=icon]:hidden">
          <SiteBuildMetadata compact className="text-muted-foreground" />
        </div>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
};

export const Route = createFileRoute("/_app/organizations/$organizationId/admin")({
  staticData: { sidebarLayout: true },
  params: {
    parse: parseOrganizationIdParams,
  },
  loader: ({ params, ...loaderContext }) =>
    ensureRouteQueryData(loaderContext, getAdminOrganizationGetOrganizationQueryOptions(params.organizationId)),
  component: OrganizationAdminPage,
});
